import { z } from "zod";
import {
	fusionMachineConfigurationSchema,
	type FusionMachine,
	type FusionMachineConfiguration,
	type FusionMachineRequest,
	type MachineCorrugateTrackWithOffset,
	fusionMachineSchema,
	prepareFusionMachineData,
} from "@packsize/schemas/machines";

import { packNetAxios } from "@/lib/api-client";

import { baseRoutes } from "../base-routes";

export async function getFusionMachines(): Promise<FusionMachine[]> {
	const response = await packNetAxios.get(`${baseRoutes.fusion}/api/v1/machines`);

	if (Array.isArray(response.data) === false) {
		throw new Error("Unable to fetch Fusion machines, response is not a list");
	}

	return z
		.array(fusionMachineSchema)
		.enrich({ callSite: "getFusionMachines - v2" })
		.parseWithTelemetry(response.data.map(prepareFusionMachineData));
}

export async function getFusionMachine(machineId: string): Promise<FusionMachine | null> {
	const response = await packNetAxios.get(`${baseRoutes.fusion}/api/v1/machines/${machineId}`);

	if (response.status !== 200) {
		return null;
	}

	return fusionMachineSchema
		.enrich({ callSite: "getFusionMachine - v2" })
		.parseWithTelemetry(prepareFusionMachineData(response.data));
}

export async function createFusionMachine(data: FusionMachineRequest): Promise<void> {
	await packNetAxios.post(`${baseRoutes.fusion}/api/v1/machines`, data);
}

export async function updateFusionMachine(
	tenantId: string,
	machineId: string,
	data: FusionMachineRequest,
): Promise<void> {
	await packNetAxios.put(`${baseRoutes.fusion}/api/v1/machines/${machineId}`, {
		id: machineId,
		tenantId,
		...data,
	});
}

export async function importFusionMachineConfiguration(file: File): Promise<FusionMachineConfiguration> {
	const formData = new FormData();

	formData.append("formFile", file);
	formData.append("fileName", file.name);

	const response = await packNetAxios.post(
		`${baseRoutes.fusion}/api/v1/machines/UploadPhysicalConfigurationFile`,
		formData,
		{
			headers: {
				"Content-Type": "multipart/form-data",
			},
		},
	);

	const parsedConfiguration = fusionMachineConfigurationSchema.safeParse(response.data);

	if (response.status !== 200 || parsedConfiguration.success === false) {
		throw new Error("Failed to load configuration file");
	}

	return parsedConfiguration.data;
}

export async function updateFusionTracks(machineId: string, tracks: MachineCorrugateTrackWithOffset[]): Promise<void> {
	await packNetAxios.put(
		`${baseRoutes.fusion}/api/v1/machines/${machineId}/tracks`,
		tracks.map((track) => ({
			number: track.number,
			loadedCorrugate: {
				id: track.assignedMaterial.id,
				alias: track.assignedMaterial.alias,
				width: track.assignedMaterial.width,
				thickness: track.assignedMaterial.thickness,
				quality: track.assignedMaterial.quality,
			},
		})),
	);
}
